<template>
  <div>
    <CommonDialog
      :dialog="dialog"
      :head-text="$t('tabs.detach-user')"
      :content-text="$t('tabs.detach-user-dialog')"
      :confirm-button="{
        text: $t('tabs.detach'),
        icon: 'mdi-link-off'
      }"
      :on-confirm="deleteUser"
      :on-cancel="
        () => {
          dialog = false;
        }
      "
    ></CommonDialog>
    <v-data-table
      :headers="headers"
      :items="users"
      :items-per-page="-1"
      hide-default-footer
      :loading="loading"
      :loading-text="$t(`tables.loading`)"
      no-data-text=""
      class="table"
      :height="isEmpty ? undefined : 'calc(100vh - 250px)'"
      :fixed-header="true"
    >
      <template v-slot:item.name="{ item }">
        <template v-if="item.type === 'newUser'">
          <div v-if="toggleOption">
            <v-row>
              <v-col>
                <v-switch
                  v-model="setUserInput"
                  color="red"
                  :label="switchLabel"
                  hide-details
                ></v-switch>
              </v-col>
              <v-col>
                <div style="position: relative; height: 60px;">
                  <v-text-field
                    ref="newUserEmail"
                    v-model="newUser.email"
                    :label="$t('tabs.user-email')"
                    :rules="[rules.required, rules.email]"
                    v-on:keyup.enter="save()"
                    v-show="!setUserInput"
                    class="userEmail"
                  ></v-text-field>
                  <v-select
                    ref="newUserGroup"
                    :items="userGroups"
                    v-model="selectedUserGroup"
                    item-text="name"
                    item-value="id"
                    :label="$t('user-groups.user-group')"
                    :rules="[rules.required]"
                    v-on:keyup.enter="save()"
                    v-show="setUserInput"
                    class="userGroup"
                  ></v-select>
                </div>
              </v-col>
              <v-col></v-col>
            </v-row>
          </div>
          <div v-else>
            <v-text-field
              ref="newUserEmail"
              height="42"
              v-model="newUser.email"
              :label="$t('tabs.user-email')"
              :rules="[rules.required, rules.email]"
              v-on:keyup.enter="save()"
            ></v-text-field>
          </div>
        </template>
        <template v-else>
          {{ item._identity.name }}
        </template>
      </template>
      <template v-slot:item.roles="{ item }">
        <template v-if="item._identity && item._identity.id === editUser.id">
          <v-select
            ref="editUserRole"
            v-model="editUser.roles"
            :items="rolesForSelect"
            :label="$t('tabs.roles')"
            multiple
            :rules="[rules.required]"
            v-on:keyup.enter="edit(item)"
          ></v-select>
        </template>
        <template v-else-if="item.type === 'newUser'">
          <v-select
            ref="newUserRole"
            v-model="newUser.roles"
            :items="rolesForSelect"
            :label="$t('tabs.roles')"
            multiple
            :rules="[rules.required]"
          ></v-select>
        </template>
        <template v-else>
          {{ getRolesNames(item._roles) }}
        </template>
      </template>
      <template v-slot:item.action="{ item }">
        <template v-if="item.type === 'newUser'">
          <v-btn
            class="ml-2 mb-2 mobile"
            color="primary"
            :loading="loading"
            v-on:click="save()"
          >
            <v-icon>
              mdi-plus
            </v-icon>
            {{ $t(`buttons.create`) }}
          </v-btn>
          <v-icon small class="mr-2 desktop" @click="save()" color="primary">
            mdi-plus
          </v-icon>
        </template>
        <template
          v-else-if="item._identity && item._identity.id === editUser.id"
        >
          <v-btn
            class="ml-2 mb-2 mobile"
            color="primary"
            :loading="loading"
            @click="edit(item)"
          >
            <v-icon>
              mdi-check
            </v-icon>
            {{ $t(`buttons.edit`) }}
          </v-btn>
          <v-icon
            small
            class="mr-2 desktop"
            @click="edit(item)"
            color="primary"
          >
            mdi-check
          </v-icon>
          <v-btn
            class="ml-2 mb-2 mobile"
            color="primary"
            :loading="loading"
            @click="closeEditUser(item)"
          >
            <v-icon>
              mdi-close
            </v-icon>
            {{ $t(`buttons.close`) }}
          </v-btn>
          <v-icon
            small
            @click="closeEditUser(item)"
            color="primary"
            class="desktop"
          >
            mdi-close
          </v-icon>
        </template>

        <template v-else>
          <v-btn
            class="ml-2 mb-2 mobile"
            color="primary"
            :loading="loading"
            @click="openEditUser(item)"
          >
            <v-icon>
              mdi-pencil
            </v-icon>
            {{ $t(`buttons.edit`) }}
          </v-btn>
          <v-icon
            small
            class="mr-2 desktop"
            @click="openEditUser(item)"
            color="primary"
          >
            mdi-pencil
          </v-icon>
          <v-btn
            class="ml-2 mb-2 mobile"
            color="primary"
            :loading="loading"
            @click="callDialog(item)"
          >
            <v-icon>
              mdi-trash-can-outline
            </v-icon>
            {{ $t(`buttons.delete`) }}
          </v-btn>
          <v-icon
            class="desktop"
            small
            @click="callDialog(item)"
            color="primary"
          >
            mdi-trash-can-outline
          </v-icon>
        </template>
      </template>
      <template v-slot:footer>
        <TableEmptyFooter v-if="isEmpty" can-add></TableEmptyFooter>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { rules } from "./../../mixins/rules";
import CommonDialog from "../dialogs/CommonDialog";
import TableEmptyFooter from "../tables/TableEmptyFooter";
export default {
  name: "Authorizations",
  props: {
    id: {
      type: String
    },
    store: {
      type: String
    }
  },
  mixins: [rules],
  components: { CommonDialog, TableEmptyFooter },
  computed: {
    isEmpty() {
      return !(this.nativeUsers && this.nativeUsers.length) && !this.loading;
    },
    roles: {
      get() {
        return this.$store.getters[`${this.$props.store}/getRoles`];
      }
    },
    nativeUsers() {
      return this.$store.getters[`${this.$props.store}/getUsers`];
    },
    users: {
      get() {
        return [{ type: "newUser" }, ...this.nativeUsers];
      }
    },
    rolesForSelect() {
      return this.roles.map(i => {
        return { value: i.id, text: i.name };
      });
    },
    switchLabel() {
      return this.setUserInput
        ? this.$t("user-groups.user-group")
        : this.$t("tabs.user-email");
    }
  },

  data: function() {
    return {
      headers: [
        {
          text: this.$i18n.t("tabs.name"),
          align: "left",
          sortable: false,
          value: "name"
        },
        {
          text: this.$i18n.t("tabs.roles"),
          value: "roles",
          sortable: false
        },
        {
          text: this.$i18n.t("tabs.action"),
          value: "action",
          align: "right",
          sortable: false
        }
      ],
      newUser: {
        email: "",
        roles: []
      },

      editUser: {
        id: "",
        roles: []
      },
      dialog: false,
      userIdToDelete: 0,
      loading: false,
      toggleOption: false,
      setUserInput: false,
      userGroups: [],
      selectedUserGroup: null,
      userTypeToDelete: null
    };
  },

  created() {
    this.setToggleOption();
    this.getUsers();
    this.$store.dispatch(`${this.$props.store}/getRoles`);
  },

  methods: {
    getUsers() {
      this.loading = true;
      this.$store
        .dispatch(`${this.$props.store}/getUsers`, { id: this.$props.id })
        .finally(() => (this.loading = false));
    },
    getRolesNames(roles) {
      let vm = this;
      return roles
        .map(role => {
          let findRoles = vm.roles.find(i => i.id === role.id) || {
            name: role.id
          };
          return findRoles.name;
        })
        .join(", ");
    },

    openEditUser(item) {
      if (item._identity.type === "UserGroup") {
        this.editUser = {
          id: item._identity.id,
          roles: item._roles.map(i => i.id),
          name: item._identity.name
        };
      } else {
        this.editUser = {
          id: item._identity.id,
          roles: item._roles.map(i => i.id),
          name: item._identity.name
        };
      }
    },
    closeEditUser() {
      this.$refs.editUserRole.resetValidation();
      this.editUser = {};
    },
    resetNewUser() {
      if (this.$refs.newUserEmail) {
        this.$refs.newUserEmail.resetValidation();
      } else {
        this.$refs.newUserGroup.resetValidation();
      }
      this.$refs.newUserRole.resetValidation();
      this.newUser = {
        email: "",
        roles: []
      };
      this.setUserInput = false;
      this.selectedUserGroup = null;
    },
    edit(item) {
      if (item._identity.type === "UserGroup") {
        this.loading = true;
        this.$store
          .dispatch(`${this.$props.store}/editUserGroup`, {
            id: this.$props.id,
            user_id: this.editUser.id,
            roles: this.editUser.roles
          })
          .finally(() => {
            this.getUsers();
          });
        this.closeEditUser();
      } else {
        if (this.$refs.editUserRole.validate()) {
          this.loading = true;
          this.$store
            .dispatch(`${this.$props.store}/editUser`, {
              id: this.$props.id,
              user_id: this.editUser.id,
              roles: this.editUser.roles
            })
            .finally(() => {
              this.getUsers();
            });
          this.closeEditUser();
        }
      }
    },

    deleteUser() {
      this.loading = true;
      if (this.userTypeToDelete === "UserGroup") {
        this.$store
          .dispatch(`${this.$props.store}/deleteUserGroup`, {
            id: this.$props.id,
            user_id: this.userIdToDelete
          })
          .finally(() => {
            this.getUsers();
          });
        this.dialog = false;
      } else {
        this.$store
          .dispatch(`${this.$props.store}/deleteUser`, {
            id: this.$props.id,
            user_id: this.userIdToDelete
          })
          .finally(() => {
            this.getUsers();
          });
        this.dialog = false;
      }
    },
    callDialog(user) {
      this.dialog = true;
      this.userIdToDelete = user._identity.id;
      this.userTypeToDelete = user._identity.type;
    },
    save() {
      this.$refs.newUserRole.validate();

      this.loading = true;

      if (this.setUserInput === false && this.$refs.newUserEmail.validate()) {
        // If email is provided, send the email
        this.$store
          .dispatch(`${this.$props.store}/addUser`, {
            id: this.$props.id,
            email: this.newUser.email,
            roles: this.newUser.roles
          })
          .finally(() => {
            this.loading = false;
            this.getUsers();
          });
      } else {
        this.$store
          .dispatch(`${this.$props.store}/addUserGroup`, {
            id: this.$props.id,
            userGroupId: this.selectedUserGroup,
            roles: this.newUser.roles
          })
          .finally(() => {
            this.loading = false;
            this.getUsers();
          });
      }
      this.resetNewUser();
    },
    async setToggleOption() {
      if (this.$props.store) {
        try {
          await this.$store.dispatch("userGroupStore/getList", {
            params: {
              limit: -1
            },
            onSuccess: () => {
              this.isLoading = false;
            }
          });

          const list = this.$store.state.userGroupStore.list;

          if (list && list.length > 0) {
            this.userGroups = list;
            this.toggleOption = true;
          } else {
            this.toggleOption = false;
          }
        } catch (error) {
          this.toggleOption = false;
        }
      } else {
        this.toggleOption = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.userGroup {
  position: absolute !important;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
}
.userEmail {
  position: absolute !important;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
}
.table::v-deep th:nth-child(2) {
  width: 50%;
}
.mobile {
  display: none;
}
.desktop {
  display: inline-flex;
}
.table::v-deep .v-data-table__mobile-row:nth-child(3) {
  min-height: 0;
  .mobile {
    display: inline-block;
  }
  .desktop {
    display: none;
  }
  .v-data-table__mobile-row__header {
    display: none;
  }
  .v-data-table__mobile-row__cell {
    width: 100% !important;
    .actions-container {
      width: 100% !important;
    }
  }
}
</style>
